<script setup lang="ts">
import { ref, computed, watch } from 'vue'

type Step = {
  title: string
  year: string
  text: string
  media: Record<string, unknown>
}

type Props = {
  steps: Step[]
}

const _props = defineProps<{ data: Props }>()
const items = computed(() => _props.data.steps)

const activeIndex = ref<number>(0)
const rangeValue = ref<number>(0)

watch(rangeValue, (val) => {
  const closestStep = Math.round(val)
  if (closestStep !== activeIndex.value) {
    activeIndex.value = closestStep
  }
})

const percentage = computed<number>(() => (rangeValue.value / (items.value.length - 1)) * 100)
</script>

<template>
  <div class="relative w-full overflow-clip lg:h-[calc(100dvh-4rem)]">
    <div class="relative aspect-square lg:aspect-auto lg:h-full lg:w-full">
      <div class="relative flex h-full w-full overflow-clip bg-(--green-1) object-cover">
        <CommonMedia
          v-for="(step, idx) in data.steps"
          :key="step.year"
          :media="step.media"
          class="absolute inset-0 h-full w-full transition-opacity duration-500 ease-in-out"
          :class="idx == activeIndex ? 'opacity-100' : 'opacity-0'"
          :sizes="'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw'"
        />
        <div class="left-bleed absolute top-5 transform lg:hidden">
          <div class="font-display text-3xl text-white drop-shadow-md">
            {{ data.steps[activeIndex]?.year }}
          </div>
        </div>

        <div class="pointer-events-none absolute inset-0 flex flex-col justify-end pb-4 lg:pb-10">
          <div class="relative h-full w-full">
            <div class="mx-10 lg:mx-bleed relative h-full">
              <div
                class="absolute bottom-15 left-0 z-10 hidden -translate-x-1/2 transform lg:flex"
                :style="{ left: `${percentage}%` }"
              >
                <div class="font-display text-3xl text-white drop-shadow-md">
                  {{ data.steps[activeIndex]?.year }}
                </div>
              </div>

              <div class="relative flex h-full w-full cursor-pointer items-center">
                <!-- range slider line -->
                <div class="absolute bottom-5 h-px w-full bg-white opacity-50 before:absolute before:-right-5 before:h-px before:w-5 before:bg-white lg:before:hidden" />

                <!-- range slider line fill -->
                <div
                  class="absolute bottom-5 h-px bg-white opacity-100 before:absolute before:-left-5 before:h-px before:w-5 before:bg-white lg:before:hidden"
                  :style="{ width: `${percentage}%` }"
                />

                <input
                  type="range"
                  :min="0"
                  :max="data.steps.length - 1"
                  step="any"
                  v-model.number="rangeValue"
                  class="pointer-events-auto absolute bottom-5 z-20 flex h-full w-full items-end opacity-100"
                />

                <!-- dragging thumb-->
                <div
                  class="pointer-events-none absolute bottom-5 -translate-x-1/2 translate-y-1/2 transform"
                  :style="{ left: `${percentage}%` }"
                >
                  <div
                    class="bg-opacity-90 flex items-center justify-center gap-2 rounded-2xl bg-(--theme-bg) px-3 py-4 text-(--theme-fg) shadow-lg lg:bg-white lg:text-(--green-1)"
                  >
                    <IconsArrowLeft class="size-4" />
                    <IconsArrowRight class="size-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <transition name="fade" mode="out-in">
        <div
          class="lg:left-bleed lg:top-bleed lg:w-cols-4 bg-(--theme-bg) p-5 text-(--theme-fg) lg:absolute lg:rounded-sm lg:bg-(--white) lg:text-(--green-1)"
          :key="activeIndex"
        >
          <div class="flex flex-col gap-10">
            <h3 class="font-display text-xl" v-if="data.steps[activeIndex]?.title">
              {{ data.steps[activeIndex]?.title }}
            </h3>
            <ElementsPortableTextWrapper
              class="mt-5 block text-base"
              :content="data.steps[activeIndex]?.text"
            />
          </div>
        </div>
      </transition>
    </div>

    <!-- range slider -->
  </div>
</template>

<style lang="postcss" scoped>
@media (max-width: 1024px) {
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
/* hide default thumb */
input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 0;
  width: 20px;
  background: transparent;
  cursor: pointer;
  background-clip: padding-box;
  border: 0.4rem solid rgba(0, 0, 0, 0);
  transform: scale(4);
}

input[type='range']::-moz-range-thumb {
  background: transparent;
  height: 0;
  width: 20px;
  cursor: pointer;
  background-clip: padding-box;
  border: 0.4rem solid rgba(0, 0, 0, 0);
  box-sizing: border-box;
  transform: scale(4);
}

input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  touch-action: none;
  opacity: 0;
}
</style>
